/**
 * POS Receipt Page
 * ================
 * 
 * Printable receipt for completed POS transactions.
 */

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Printer,
  Mail,
  ArrowLeft,
  Check,
  Loader2,
  Download,
} from "lucide-react";

const POSReceiptPage = () => {
  const { transactionId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const receiptRef = useRef(null);
  
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  
  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const res = await fetch(`${API}/pos/transactions/${transactionId}/receipt`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || "Failed to load receipt");
        }
        
        const data = await res.json();
        setReceipt(data);
      } catch (err) {
        console.error("Error fetching receipt:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (transactionId) {
      fetchReceipt();
    }
  }, [transactionId, token]);
  
  const handlePrint = async () => {
    // Mark as printed
    try {
      await fetch(`${API}/pos/transactions/${transactionId}/receipt/print`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Error marking printed:", err);
    }
    
    // Print the receipt
    window.print();
  };
  
  const handleEmailReceipt = async () => {
    if (!emailAddress) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSendingEmail(true);
    
    try {
      const res = await fetch(`${API}/pos/transactions/${transactionId}/receipt/email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email: emailAddress })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to send email");
      }
      
      toast.success(`Receipt sent to ${emailAddress}`);
      setShowEmailModal(false);
      setEmailAddress("");
      
      // Update local state
      setReceipt(prev => ({ ...prev, emailed: true }));
    } catch (err) {
      console.error("Email error:", err);
      toast.error(err.message || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };
  
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center text-gray-900">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Link to="/merchant/pos">
            <Button className="bg-indigo-600 hover:bg-indigo-700">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to POS
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <>
      {/* Screen UI - hidden when printing */}
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 print:hidden">
        <div className="max-w-2xl mx-auto">
          {/* Header Actions */}
          <div className="flex items-center justify-between mb-6">
            <Link to="/merchant/pos" className="text-gray-500 hover:text-gray-900 flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to POS
            </Link>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowEmailModal(true)}
                className="border-gray-200"
                data-testid="email-receipt-btn"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email Receipt
                {receipt?.emailed && <Check className="h-3 w-3 ml-1 text-emerald-400" />}
              </Button>
              <Button
                onClick={handlePrint}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="print-receipt-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Receipt
              </Button>
            </div>
          </div>
          
          {/* Receipt Preview (styled for screen) */}
          <div className="bg-white rounded-xl p-8 text-slate-900" ref={receiptRef}>
            <ReceiptContent receipt={receipt} formatPrice={formatPrice} formatDate={formatDate} />
          </div>
        </div>
      </div>
      
      {/* Print-only Receipt */}
      <div className="hidden print:block">
        <div className="max-w-[80mm] mx-auto p-2 text-xs">
          <ReceiptContentPrint receipt={receipt} formatPrice={formatPrice} formatDate={formatDate} />
        </div>
      </div>
      
      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="bg-white border-gray-200 max-w-sm">
          <DialogHeader>
            <DialogTitle>Email Receipt</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm text-gray-500">Customer Email</label>
            <Input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="customer@email.com"
              className="bg-gray-100 border-gray-200 mt-1"
              data-testid="email-input"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEmailModal(false)}
              className="border-gray-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEmailReceipt}
              disabled={sendingEmail || !emailAddress}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Receipt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: 80mm auto;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
          }
        }
      `}</style>
    </>
  );
};

// Receipt content for screen display
const ReceiptContent = ({ receipt, formatPrice, formatDate }) => (
  <div className="space-y-6">
    {/* Header */}
    <div className="text-center border-b border-slate-200 pb-4">
      <h1 className="text-2xl font-bold text-indigo-600">
        {receipt?.merchant?.name || "Store"}
      </h1>
      {receipt?.merchant?.address && (
        <p className="text-gray-500 text-sm mt-1">{receipt.merchant.address}</p>
      )}
      {receipt?.merchant?.phone && (
        <p className="text-gray-500 text-sm">{receipt.merchant.phone}</p>
      )}
    </div>
    
    {/* Receipt Info */}
    <div className="bg-slate-50 rounded-lg p-4">
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-gray-500">Receipt #:</span>
          <span className="ml-2 font-medium">{receipt?.receipt_number}</span>
        </div>
        <div>
          <span className="text-gray-500">Date:</span>
          <span className="ml-2">{formatDate(receipt?.date)}</span>
        </div>
        <div>
          <span className="text-gray-500">Transaction:</span>
          <span className="ml-2 font-mono text-xs">{receipt?.transaction_number}</span>
        </div>
        <div>
          <span className="text-gray-500">Payment:</span>
          <span className="ml-2 capitalize">{receipt?.payment?.method || "N/A"}</span>
        </div>
      </div>
    </div>
    
    {/* Items */}
    <div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2 text-gray-500 font-medium">Item</th>
            <th className="text-center py-2 text-gray-500 font-medium">Qty</th>
            <th className="text-right py-2 text-gray-500 font-medium">Price</th>
            <th className="text-right py-2 text-gray-500 font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {receipt?.items?.map((item, idx) => (
            <tr key={idx} className="border-b border-slate-100">
              <td className="py-2">{item.name}</td>
              <td className="py-2 text-center">{item.quantity}</td>
              <td className="py-2 text-right">{formatPrice(item.unit_price)}</td>
              <td className="py-2 text-right">{formatPrice(item.total_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    
    {/* Totals */}
    <div className="border-t border-slate-200 pt-4 space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-500">Subtotal</span>
        <span>{formatPrice(receipt?.totals?.subtotal || 0)}</span>
      </div>
      {receipt?.totals?.discount_amount > 0 && (
        <div className="flex justify-between text-sm text-emerald-600">
          <span>Discount</span>
          <span>-{formatPrice(receipt.totals.discount_amount)}</span>
        </div>
      )}
      <div className="flex justify-between text-sm">
        <span className="text-gray-500">VAT (20%)</span>
        <span>{formatPrice(receipt?.totals?.tax_amount || 0)}</span>
      </div>
      <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2">
        <span>Total</span>
        <span className="text-indigo-600">{formatPrice(receipt?.totals?.total || 0)}</span>
      </div>
      {receipt?.payment?.change_due > 0 && (
        <div className="flex justify-between text-sm bg-emerald-50 p-2 rounded">
          <span className="text-emerald-600">Change Given</span>
          <span className="text-emerald-600 font-medium">{formatPrice(receipt.payment.change_due)}</span>
        </div>
      )}
    </div>
    
    {/* Footer */}
    <div className="text-center text-gray-500 text-xs pt-4 border-t border-slate-100">
      <p>Thank you for your purchase!</p>
      
      {/* Feedback QR Code */}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-slate-600 font-medium mb-2">Rate your experience</p>
        <div className="flex justify-center">
          <img 
            src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`${window.location.origin}/feedback/${receipt?.merchant?.id}/${receipt?.transaction_id}`)}`}
            alt="Feedback QR Code"
            className="w-24 h-24"
          />
        </div>
        <p className="text-[10px] text-gray-500 mt-2">Scan to leave feedback</p>
      </div>
      
      <p className="mt-4">Powered by Veyronix AI POS</p>
    </div>
  </div>
);

// Receipt content optimized for thermal printer (80mm)
const ReceiptContentPrint = ({ receipt, formatPrice, formatDate }) => (
  <div className="font-mono">
    {/* Header */}
    <div className="text-center border-b border-dashed border-black pb-2 mb-2">
      <div className="text-sm font-bold">{receipt?.merchant?.name || "Store"}</div>
      {receipt?.merchant?.address && (
        <div className="text-[10px]">{receipt.merchant.address}</div>
      )}
      {receipt?.merchant?.phone && (
        <div className="text-[10px]">{receipt.merchant.phone}</div>
      )}
    </div>
    
    {/* Receipt Info */}
    <div className="text-[10px] mb-2">
      <div>Receipt: {receipt?.receipt_number}</div>
      <div>Date: {formatDate(receipt?.date)}</div>
      <div>Txn: {receipt?.transaction_number}</div>
    </div>
    
    {/* Items */}
    <div className="border-t border-dashed border-black pt-1 mb-1">
      {receipt?.items?.map((item, idx) => (
        <div key={idx} className="flex justify-between text-[10px]">
          <span className="flex-1 truncate">{item.quantity}x {item.name}</span>
          <span className="ml-2">{formatPrice(item.total_amount)}</span>
        </div>
      ))}
    </div>
    
    {/* Totals */}
    <div className="border-t border-dashed border-black pt-1 text-[10px]">
      <div className="flex justify-between">
        <span>Subtotal</span>
        <span>{formatPrice(receipt?.totals?.subtotal || 0)}</span>
      </div>
      {receipt?.totals?.discount_amount > 0 && (
        <div className="flex justify-between">
          <span>Discount</span>
          <span>-{formatPrice(receipt.totals.discount_amount)}</span>
        </div>
      )}
      <div className="flex justify-between">
        <span>VAT (20%)</span>
        <span>{formatPrice(receipt?.totals?.tax_amount || 0)}</span>
      </div>
      <div className="flex justify-between font-bold text-xs border-t border-black mt-1 pt-1">
        <span>TOTAL</span>
        <span>{formatPrice(receipt?.totals?.total || 0)}</span>
      </div>
      {receipt?.payment?.change_due > 0 && (
        <div className="flex justify-between mt-1">
          <span>Change</span>
          <span>{formatPrice(receipt.payment.change_due)}</span>
        </div>
      )}
      <div className="flex justify-between mt-1">
        <span>Payment</span>
        <span className="capitalize">{receipt?.payment?.method}</span>
      </div>
    </div>
    
    {/* Footer */}
    <div className="text-center text-[9px] mt-3 border-t border-dashed border-black pt-2">
      <div>Thank you!</div>
      {/* Feedback QR Code for print */}
      <div className="mt-2">
        <div className="text-[8px]">Rate us:</div>
        <img 
          src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=${encodeURIComponent(`${window.location.origin}/feedback/${receipt?.merchant?.id}/${receipt?.transaction_id}`)}`}
          alt="QR"
          className="w-12 h-12 mx-auto my-1"
        />
      </div>
      <div className="mt-1">Powered by Veyronix</div>
    </div>
  </div>
);

export default POSReceiptPage;
